import React, { useEffect, useState } from 'react';
import { getPosition } from './api';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(val) {
  if (val == null) return '—';
  return '$' + parseFloat(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function statusBadge(status) {
  const map = {
    OPEN_PUT: { label: 'Open put', cls: 'badge-put' },
    OPEN_CALL: { label: 'Open call', cls: 'badge-call' },
    ASSIGNED: { label: 'Assigned', cls: 'badge-assigned' },
    PENDING_ASSIGNMENT: { label: 'Pending', cls: 'badge-assigned' },
    PARTIAL_ASSIGNED: { label: 'Partial', cls: 'badge-assigned' },
    CLOSED: { label: 'Closed', cls: 'badge-closed' },
    EXPIRED: { label: 'Expired', cls: 'badge-expired' },
  };
  const s = map[status] || { label: status, cls: 'badge-closed' };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

function regimeBadge(flag) {
  if (!flag) return '—';
  const labels = { UPTREND: 'Uptrend', SIDEWAYS: 'Sideways', DOWNTREND: 'Downtrend', VOLATILE: 'Volatile' };
  return <span className={`regime regime-${flag}`}>{labels[flag] || flag}</span>;
}

export default function PositionDetail({ positionId, onBack }) {
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getPosition(positionId)
      .then(res => setPosition(res.data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [positionId]);

  if (loading) return <p className="loading">Loading position...</p>;
  if (error) return <p className="error">Error: {error}</p>;
  if (!position) return <p className="error">Position not found.</p>;

  const p = position;

  return (
    <>
      <button className="back-btn" onClick={onBack}>← Back to ledger</button>

      <div className="detail-header">
        <span className="detail-ticker">{p.ticker}</span>
        {statusBadge(p.status)}
        {regimeBadge(p.current_regime_flag)}
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Capital at risk</div>
          <div className="stat-value">{formatCurrency(p.total_capital_at_risk)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Premium collected</div>
          <div className="stat-value" style={{ color: '#4ade80' }}>{formatCurrency(p.total_premium_collected)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Days in trade</div>
          <div className="stat-value">{p.days_in_trade ?? '—'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Regime at open</div>
          <div style={{ paddingTop: 6 }}>{regimeBadge(p.regime_flag_at_open)}</div>
        </div>
      </div>

      {p.status === 'ASSIGNED' && (
        <div className="stats-grid" style={{ marginTop: -20 }}>
          <div className="stat-card">
            <div className="stat-label">Cost basis / share</div>
            <div className="stat-value">{formatCurrency(p.cost_basis_per_share)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Shares held</div>
            <div className="stat-value">{p.shares_held}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Sector</div>
            <div className="stat-value" style={{ fontSize: 16 }}>{p.sector_bucket || '—'}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Opened</div>
            <div className="stat-value" style={{ fontSize: 16 }}>{formatDate(p.opened_at)}</div>
          </div>
        </div>
      )}

      <div className="legs-section">
        <div className="section-label">Legs</div>
        {(!p.legs || p.legs.length === 0) ? (
          <p className="empty">No legs recorded.</p>
        ) : (
          p.legs.map(leg => (
            <div key={leg.leg_id} className="leg-card">
              <div>
                <div className="leg-field-label">Type</div>
                <div className="leg-field-value">{leg.leg_type.replace('_', ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase())}</div>
                <div style={{ marginTop: 8 }}>
                  <div className="leg-field-label">Decision</div>
                  <div className="leg-field-value" style={{ fontSize: 11, color: leg.decision_type === 'SYSTEM_ALIGNED' ? '#4ade80' : '#fbbf24' }}>
                    {leg.decision_type.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase())}
                  </div>
                </div>
              </div>
              <div>
                <div className="leg-field-label">Strike</div>
                <div className="leg-field-value">{leg.strike ? `$${parseFloat(leg.strike).toFixed(2)}` : '—'}</div>
                <div style={{ marginTop: 8 }}>
                  <div className="leg-field-label">Expiration</div>
                  <div className="leg-field-value">{formatDate(leg.expiration_date)}</div>
                </div>
              </div>
              <div>
                <div className="leg-field-label">Premium expected</div>
                <div className="leg-field-value">{leg.premium_expected ? `$${parseFloat(leg.premium_expected).toFixed(2)}` : '—'}</div>
                <div style={{ marginTop: 8 }}>
                  <div className="leg-field-label">Premium actual</div>
                  <div className="leg-field-value" style={{ color: '#4ade80' }}>{leg.premium_actual ? `$${parseFloat(leg.premium_actual).toFixed(2)}` : '—'}</div>
                </div>
              </div>
              <div>
                <div className="leg-field-label">Slippage</div>
                <div className="leg-field-value">
                  {leg.slippage_pct != null ? `${(parseFloat(leg.slippage_pct) * 100).toFixed(1)}%` : '—'}
                  {leg.slippage_bucket && (
                    <span style={{ marginLeft: 6, fontSize: 10, color: leg.slippage_bucket === 'NORMAL' ? '#4ade80' : '#fbbf24' }}>
                      {leg.slippage_bucket}
                    </span>
                  )}
                </div>
                <div style={{ marginTop: 8 }}>
                  <div className="leg-field-label">Outcome</div>
                  <div className="leg-field-value">{leg.outcome}</div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
